import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { Button } from './primitives'

/**
 * Asking "are you sure" in the app's own voice.
 *
 * `window.confirm` was doing this, and it is the wrong thing in a client-facing tool for
 * three reasons. It looks like the browser rather than like the tracker, which at the moment
 * somebody is about to delete something is exactly when the tool should look most like
 * itself. It cannot say more than one line, so the consequence — that a comment has replies,
 * that a product takes its answers with it — had to be crammed into the same sentence as the
 * question. And it freezes the page: nothing renders, no toast can appear, and on some
 * browsers a second one is silently suppressed.
 *
 * The API is a promise, so a call site reads the same as the thing it replaced:
 *
 *     if (!await confirm({ title: 'Delete this comment?' })) return
 */

export type ConfirmOptions = {
  title: string
  /** What will actually happen. Worth a sentence — this is the part `window.confirm` lost. */
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Styles the button as destructive and focuses Cancel instead. */
  danger?: boolean
}

type Ask = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<Ask | null>(null)

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void }

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  const ask = useCallback<Ask>(
    (options) => new Promise<boolean>((resolve) => { setPending({ ...options, resolve }) }),
    [],
  )

  const settle = useCallback((ok: boolean) => {
    setPending((current) => { current?.resolve(ok); return null })
  }, [])

  useEffect(() => {
    if (!pending) return
    // Cancel is what Escape means, and what the focus starts on when the action is
    // destructive: the safe answer should be the one a stray Return gives.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') settle(false) }
    document.addEventListener('keydown', onKey)
    const focus = pending.danger ? cancelRef.current : confirmRef.current
    focus?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [pending, settle])

  const value = useMemo(() => ask, [ask])

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* A backdrop, not a control: as a button it was a second thing called "Cancel"
              in the same dialog, which is confusing to hear and pointless to reach — the
              keyboard already has Escape and the Cancel button itself. */}
          <div data-confirm-backdrop aria-hidden className="fixed inset-0 bg-black/50"
               onClick={() => settle(false)} />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            className="relative w-full max-w-md rounded-lg border border-mc-line bg-mc-surface p-5 shadow-xl"
          >
            <div className="flex items-start gap-3">
              {pending.danger && (
                <AlertTriangle size={20} className="mt-0.5 shrink-0 text-mc-critical" aria-hidden />
              )}
              <div className="min-w-0">
                <h2 id="confirm-title" className="text-base font-medium text-mc-ink">
                  {pending.title}
                </h2>
                {pending.body && (
                  <div className="mt-1.5 text-sm text-mc-body">{pending.body}</div>
                )}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button ref={cancelRef} onClick={() => settle(false)}>
                {pending.cancelLabel ?? 'Cancel'}
              </Button>
              <Button ref={confirmRef} variant={pending.danger ? 'danger' : 'primary'}
                      onClick={() => settle(true)}>
                {pending.confirmLabel ?? 'Confirm'}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext)
  if (!ask) throw new Error('useConfirm must be used inside a ConfirmProvider')
  return ask
}
